import * as React from 'react';
import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useAppTranslation } from '../../../services/i18n';
import { CARD_BORDER, SECTION_BACK } from '../colors';

export type RejectedFile = {
  name: string;
  /** The partner's own status word — `file_corrupted`, `download_failed`. */
  reason?: string;
};

type Props = {
  files: RejectedFile[];
  /** How many sources there were in total, for "N z M". */
  total: number;
};

/**
 * The amber-ruled block naming the sources that did not make it into the
 * course.
 *
 * Two callers with different evidence: during the wait it carries the files
 * whose upload failed, which we know at dispatch; after the evaluation it
 * carries the ones the partner could not read, which is the set the design
 * draws. Both are "files that dropped out", and the block says which by
 * spelling out the reason behind "Zobrazit důvody".
 */
export function RejectedSources({ files, total }: Props) {
  const t = useAppTranslation();
  const [showReasons, setShowReasons] = useState(false);
  if (files.length === 0) return null;

  const hasReasons = files.some((file) => file.reason);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        backgroundColor: SECTION_BACK,
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: '8px',
        // The rule is the whole left edge, so it has to sit inside the rounded
        // corners rather than being a border on the box.
        overflow: 'hidden',
        // Spacing is the caller's: this sits inside the process card on one
        // screen and between sections on the other.
      }}
    >
      <Box
        sx={{ width: '4px', alignSelf: 'stretch', backgroundColor: 'var(--color-warning)' }}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0, padding: '16px 0 16px 2px' }}>
        <Typography
          sx={{ fontWeight: 700, fontSize: '15px', color: 'var(--color-black)' }}
        >
          {t('klara.evaluation.rejectedTitle', { count: files.length, total })}
        </Typography>
        <Typography
          sx={{ fontSize: '13px', color: 'var(--color-black-text)', marginTop: '6px' }}
        >
          {t('klara.evaluation.rejectedBody')}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
          {files.map((file, index) => (
            <Box
              // Not the name: the partner's per-file verdict carries an
              // optional filename, so two nameless rejects would collide.
              key={`${file.name}-${index}`}
              sx={{
                backgroundColor: 'var(--color-white)',
                border: `1px solid ${CARD_BORDER}`,
                borderRadius: '4px',
                padding: '4px 8px',
              }}
            >
              <Typography
                sx={{ fontSize: '12px', color: 'var(--color-black-text)' }}
              >
                {file.name}
              </Typography>
              {showReasons && file.reason && (
                <Typography
                  sx={{ fontSize: '12px', color: 'var(--color-black-text)', opacity: 0.8 }}
                >
                  {file.reason}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* The reasons are the partner's per-file status, which is a word rather
          than a document — so this reveals them in place instead of sending the
          user to the protocol the band already offers. */}
      {hasReasons && !showReasons && (
        <Button
          size="small"
          onClick={() => setShowReasons(true)}
          sx={{ flexShrink: 0, marginRight: '18px' }}
        >
          {t('klara.evaluation.showReasons')}
        </Button>
      )}
    </Box>
  );
}
